import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { patchChildToniResult, postChildToniResult, useGetChildToniResultDetail } from '../../api/toni_result_api';
import { Col, Container, FormGroup, FormLabel, Row, Spinner } from 'react-bootstrap';
import { Form as FormikForm, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { ChildToniResultPatch, ChildToniResultPost } from '../../typings/types-to-send';
import { Mode, parseMode } from '../../shared/helpers/modeHelper';
import { useTranslation } from 'react-i18next';
import FormikDateInput from '../../shared/formik/FormikDateInput/FormikDateInput';
import FormikSelect, { FormikSetFunction, OnChangeValueSingle } from '../../shared/formik/FormikSelect/FormikSelect';
import { createChildOptions, useGetChildren } from '../../api/child_api';
import {
  getChildObjectFromChildId,
  getHomeIdFromChildId,
  setHomeOnChildChange,
  useGetChildIdFromSearchParams,
} from '../../shared/helpers/formHelper';
import EditToggle from '../../shared/components/EditToggle/EditToggle';
import DefaultPageActionButtons, {
  useNavigateAfterCancel,
  useNavigateToSuccessPage,
  useOnPageBack,
} from '../../shared/components/DefaultPageActionButtons/DefaultPageActionButtons';
import FormikTextInput from '../../shared/formik/FormikTextInput/FormikTextInput';
import { Domain, getDomainPermission, hasPagePermission, usePermissions } from '../../api/permissions_api';
import NoPermissionPage from '../../shared/pages/NoPermissionPage/NoPermissionPage';
import ErrorPage from '../../shared/pages/ErrorPage/ErrorPage';
import { CLASS_LEVEL_OPTIONS } from '../../shared/helpers/classLevelHelper';

type ChildToniResultProps = {};

const DOMAIN_URL = 'child-toni-result';
const DOMAIN = Domain.CHILD_TONI_RESULT;
const CHILD_TAB = 'education';

const CHILD_TONI_RESULT_FORM_SCHEMA = Yup.object().shape({
  home: Yup.number().required('Required'),
  date: Yup.date().required('Required'),

  child: Yup.number().required('Required'),

  class_at_time: Yup.number().nullable(),

  raw_score: Yup.number().nullable().optional(),
  index_score: Yup.number().required('Required'),
  percentage_score: Yup.number().min(0).max(100).required('Required'),
  description: Yup.string().required('Required'),
});

const TONI_DESCRIPTION_OPTIONS = [
  {
    label: 'Very Superior',
    value: 'Very Superior',
  },
  {
    label: 'Superior',
    value: 'Superior',
  },
  {
    label: 'Above Average',
    value: 'Above Average',
  },
  {
    label: 'Average',
    value: 'Average',
  },
  {
    label: 'Below Average',
    value: 'Below Average',
  },
  {
    label: 'Poor',
    value: 'Poor',
  },
];

function getBlankChildCheckIn(): ChildToniResultPost {
  return {
    home: undefined,
    date: '',

    child: undefined,

    class_at_time: null,

    raw_score: undefined,
    index_score: undefined,
    percentage_score: undefined,
    description: undefined,
  };
}

const ChildToniResultPage = (props: ChildToniResultProps) => {
  let { id: idString, mode: modeString } = useParams();
  const childId = useGetChildIdFromSearchParams();
  const { t } = useTranslation();
  // const { data: homeData } = getHomes();
  const { data: childData, isLoading: isChildrenLoading } = useGetChildren();
  const mode = parseMode(modeString);
  const [serverError, setServerError] = useState(false);
  const { isLoadingPermissions, permissionMap } = usePermissions();
  const onBack = useOnPageBack(DOMAIN_URL, CHILD_TAB);
  const navigateToSuccessPage = useNavigateToSuccessPage(DOMAIN_URL, CHILD_TAB);
  const navigateAfterCancel = useNavigateAfterCancel(DOMAIN_URL, CHILD_TAB);

  const hasNumberId = !!idString && !Number.isNaN(parseInt(idString));

  let id: number | undefined = undefined;
  if (idString) {
    id = parseInt(idString);
  }
  const { data, isLoading, error, refetch } = useGetChildToniResultDetail(id, mode !== Mode.CREATE && hasNumberId);

  if (isLoadingPermissions) {
    return <Spinner animation="border" role="status" />;
  } else if (!permissionMap) {
    return <ErrorPage />;
  } else if (!hasPagePermission(mode, getDomainPermission(permissionMap, DOMAIN))) {
    return <NoPermissionPage />;
  }
  const hasChangePermission = getDomainPermission(permissionMap, DOMAIN).canChange;

  let childResult: ChildToniResultPost | ChildToniResultPatch;
  let onSubmit;
  if (mode === Mode.CREATE) {
    childResult = getBlankChildCheckIn();
    if (isChildrenLoading) {
      return <Spinner animation="border" role="status" />;
    } else if (!childData) {
      return <div>Could not load children</div>;
    }
    if (childId) {
      const child = getChildObjectFromChildId(childId, childData);
      const homeId = getHomeIdFromChildId(childId, childData);
      childResult.home = homeId;
      childResult.child = childId;
      childResult.class_at_time = child.current_class;
    }
    onSubmit = async (dataToSubmit: ChildToniResultPost, { setSubmitting }: any) => {
      try {
        await postChildToniResult(dataToSubmit);
        // Let the refresh happen when the new page loads (hopefully!)
        setSubmitting(false);
        navigateToSuccessPage();
      } catch (error) {
        setServerError(true);
      }
    };
  } else if (hasNumberId) {
    if (isLoading) {
      return <Spinner animation="border" role="status" />;
    } else if (!data) {
      return <div>Could not find data for {id}</div>;
    } else if (error) {
      return <div>Error loading {id}</div>;
    } else if (serverError) {
      return <div> Something went wrong, contact LJI IT </div>;
    }

    childResult = data as ChildToniResultPatch;

    onSubmit = async (dataToSubmit: ChildToniResultPatch, { setSubmitting }: any) => {
      try {
        await patchChildToniResult(id, dataToSubmit);
        await refetch();
        setSubmitting(false);
        navigateToSuccessPage();
      } catch (error) {
        setServerError(true);
      }
    };
  } else {
    throw Error('Bad id: ' + idString);
  }

  function handleOnCancel(resetForm: (dataToResetTo: any) => void) {
    resetForm(childResult);
    navigateAfterCancel();
  }

  // const homeOptions = createHomeOptions(homeData);
  const childOptions = createChildOptions(childData);

  const handleChildChange = (newChild: OnChangeValueSingle<number>, formikSetFieldValue: FormikSetFunction) => {
    setHomeOnChildChange(newChild, childData, formikSetFieldValue);
    const child = getChildObjectFromChildId(newChild?.value, childData);
    formikSetFieldValue('class_at_time', child.current_class);
  };

  return (
    <Formik initialValues={childResult} validationSchema={CHILD_TONI_RESULT_FORM_SCHEMA} onSubmit={onSubmit}>
      {(formikProps: FormikProps<ChildToniResultPost | ChildToniResultPatch>) => {
        return (
          <FormikForm>
            <Container>
              <Row>
                <Col>
                  <h1 className="text-center font-weight-bold">{t('toniResult.DETAIL_TITLE')}</h1>
                </Col>
              </Row>
              <Row className={'shadow'}>
                <Col sm={10}>
                  <FormikSelect
                    name={'child'}
                    label={t('common.CHILD')}
                    options={childOptions}
                    onChange={handleChildChange}
                    isReadOnly={mode === Mode.VIEW}
                    labelWidth={4}
                    showSideBySide={true}
                  />
                  <FormikDateInput
                    name={'date'}
                    label={t('common.DATE')}
                    formikProps={formikProps}
                    isReadOnly={mode === Mode.VIEW}
                    labelWidth={4}
                    showSideBySide={true}
                  />
                  <FormikSelect
                    name="class_at_time"
                    label={t('toniResult.CLASS_AT_TIME')}
                    options={CLASS_LEVEL_OPTIONS}
                    isReadOnly={mode === 'view'}
                    labelWidth={4}
                    showSideBySide={true}
                  />
                  {mode === 'view' && childResult.age_at_time !== undefined && (
                    <FormGroup as={Row}>
                      <FormLabel column sm={4}>
                        {t('toniResult.AGE_AT_TIME')}
                      </FormLabel>
                      <Col>{childResult.age_at_time}</Col>
                    </FormGroup>
                  )}

                  <br />
                  <FormikTextInput
                    formikProps={formikProps}
                    name={'raw_score'}
                    label={t('toniResult.RAW_SCORE')}
                    isReadOnly={mode === Mode.VIEW}
                    labelWidth={4}
                    showSideBySide={true}
                  />
                  <FormikTextInput
                    formikProps={formikProps}
                    name={'index_score'}
                    label={t('toniResult.INDEX_SCORE')}
                    isReadOnly={mode === Mode.VIEW}
                    labelWidth={4}
                    showSideBySide={true}
                  />
                  <FormikTextInput
                    formikProps={formikProps}
                    name={'percentage_score'}
                    label={t('toniResult.PERCENTAGE_SCORE')}
                    isReadOnly={mode === Mode.VIEW}
                    labelWidth={4}
                    showSideBySide={true}
                  />
                  <FormikSelect
                    name={'description'}
                    label={t('toniResult.DESCRIPTION')}
                    options={TONI_DESCRIPTION_OPTIONS}
                    isReadOnly={mode === Mode.VIEW}
                    labelWidth={4}
                    showSideBySide={true}
                  />
                </Col>
                <Col sm={2}>
                  <EditToggle
                    show={hasChangePermission}
                    mode={mode}
                    formikProps={formikProps}
                    toEditLink={{
                      pathname: '../child-toni-result/edit/' + idString,
                      search: childId ? '?childId=' + childId : undefined,
                    }}
                    onCancel={handleOnCancel.bind(null, formikProps.resetForm)}
                  />
                </Col>
                <DefaultPageActionButtons
                  mode={mode}
                  formikProps={formikProps}
                  onCancelEdit={handleOnCancel.bind(null, formikProps.resetForm)}
                  onBack={onBack}
                />
              </Row>
            </Container>
          </FormikForm>
        );
      }}
    </Formik>
  );
};

export default ChildToniResultPage;
export { TONI_DESCRIPTION_OPTIONS };
